<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'

import { T } from '@tolgee/vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import type { AchievementItem } from '@/types/achievements'
import router from '@/router'
import { RouteName } from '@/routes'
import AchievementCard from '@/components/AchievementCard.vue'
import { useConfetti } from '@/composables/game/confetti'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'

const props = defineProps<{
	show: boolean
	categoryTitle?: string
	achievement?: AchievementItem
}>()

const emit = defineEmits<{
	(e: 'hide'): void
}>()

const { topStyle } = useFullScreenDimensions()

useWatchOverlays(computed(() => props.show))

const { showConfetti } = useConfetti()
const isClickable = ref(false)

function onContainerClick() {
  if (isClickable.value) {
    emit('hide')
  }
}

watch(() => props.show, (show) => {
  if (show) {
    showConfetti()
    setTimeout(() => isClickable.value = true, 750)
  } else {
    isClickable.value = false
  }
}, { immediate: true })
</script>
<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show && achievement && categoryTitle"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-dark-most-bottom-layer/[.65] backdrop-blur-[4px] flex flex-col z-[810] px-4"
      @click="onContainerClick"
    >
      <CrossCircleIcon
        class="w-6 h-6 min-w-[24px] absolute top-3 right-3 text-white/[87%]"
        :style="topStyle"
      />
      <div
        class="h-full w-full flex-1 flex flex-col gap-y-8 items-center justify-center mb-[60px] min-[390px]:mb-[80px]"
      >
        <div class="w-full flex flex-col items-center justify-center relative">
          <div
            class="z-[2] text-[36px] leading-[42px] font-bold max-w-[350px] text-center"
          >
            <T
              key-name="achievements.overlay.title"
              default-value="Achievement unlocked!"
            />
          </div>
          <div class="z-[2] text-center mt-3 max-[380px]:max-w-[350px] max-w-[370px] flex items-center flex-col">
            <div class="text-center text-light-white text-base font-semibold -tracking-[0.32px]">
              {{ categoryTitle }} - {{ achievement.number }}
              <template v-if="achievement.label">
                {{ ' ' }} {{ achievement.label }}
              </template>
            </div>
            <div class="text-center text-light-white text-base font-medium -tracking-[0.32px]">
              <T
                key-name="achievements.overlay.description1"
                default-value="Head to your"
              />
              {{ ' ' }}
              <span
                class="underline"
                @click.stop="router.push({name: RouteName.PROFILE}); emit('hide')"
              >
                <T
                  key-name="achievements.overlay.description2"
                  default-value="profile"
                />
              </span>
              {{ ' ' }}
              <T
                key-name="achievements.overlay.description3"
                default-value="to see all your achievements."
              />
            </div>
          </div>
          <div class="mt-6 relative flex flex-col items-center justify-center w-full">
            <div
              class="w-[90%] h-0 pb-[90%] absolute rounded-full bg-green-primary/[80] blur-[100px]"
            />
            <img
              class="absolute w-full h-auto"
              src="@/assets/images/golden-wheel-background.png"
            >

            <AchievementCard
              class="w-[173px]"
              card-class="h-[212px] rounded-[24px] p-0.5"
              image-class="h-[123px]"
              wrapper-class="gap-y-2 rounded-[23px]"
              label-class="text-[31px] leading-[46px] -tracking-[0.61]"
              :card="achievement"
              owned
            />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">

</style>